<template>
  <div class="row">
    <div class="col-md-3">
      <form class="needs-validation" ref="form" id="form">
        <div class="form-group">
          <!-- part for operatorid -->
          <label class="mb-0" for="Operator">Operator: </label>
          <select class="form-control" v-model="operator" required>
            <option
              v-for="operator in operators"
              :key="operator.id"
              :value="operator.id"
            >
              {{ operator.username }}
            </option>
          </select>
        </div>

        <!-- part for Charge-Nr. -->
        <div class="form-group">
          <label class="mb-0" for="BatchNumber">Batch Nummer: </label>
          <input
            type="text"
            class="form-control"
            v-model="batchNumber"
            id="BatchNumber"
            required
          />
        </div>

        <!-- datePicker for productiondate -->
        <div class="mb-3 form-group">
          <label class="mb-0" for="production">{{ datePickerLabel }}</label>
          <VueDatePicker
            class="b-form-datepicker"
            v-model="productionDate"
            :enable-time-picker="false"
            required
            >{{ productionDate }}</VueDatePicker
          >
        </div>

        <!-- part for isGoodEntrance -->
        <div class="mb-2 form-check">
          <input
            class="form-check-input"
            type="checkbox"
            v-model="isGoodEntrance"
            value=""
            id="goodEntrance"
          />
          <label class="form-check-label" for="goodEntrance">
            Wareneingang
          </label>
        </div>

        <!-- part for A or B Components -->
        <div class="form-group">
          <label class="mb-2">A- oder B-Komponente: </label>
          <div v-for="(component, id) in components" :key="id">
            <input
              type="radio"
              :id="id"
              v-model="selectedComponent"
              :value="component.value"
              class="radio-input"
            />
            <label :for="id">{{ component.text }}</label>
          </div>
        </div>

        <button
          type="button"
          class="btn btn-primary mb-3 mt-4"
          @click="submit()"
        >
          Speichern
        </button>
      </form>

      <!-- success alert -->
      <transition name="alert">
        <div
          class="alert alert-success mt-1"
          role="alert"
          v-if="triggerSuccess"
        >
          Der Eintrag wurde gespeichert!
        </div>
      </transition>

      <!-- failed alert -->
      <transition name="alert">
        <div class="alert alert-danger mt-1" role="alert" v-if="triggerError">
          Eintrag konnte nicht gespeichert werden!
        </div>
      </transition>

      <!-- form invalid alert -->
      <transition name="alert">
        <div
          class="alert alert-warning mt-1"
          role="alert"
          v-if="triggerWarning"
        >
          Bitte füllen Sie alle Felder aus!
        </div>
      </transition>
    </div>

    <div class="col-md-9">
      <PottingTable :url="props.tableUrl" :key="componentKey" />
    </div>
  </div>

  <!-- date expired Modal part -->
  <transition name="modal">
    <template v-if="showModal && triggerDateExpired">
      <MyModal :msg="modalMessage" :visible="showModal">
        <template #success>
          <button
            type="button"
            id="success"
            class="btn btn-success"
            @click="handleModalButton(true)"
          >
            Speichern
          </button>
        </template>
        <template #declined>
          <button
            type="button"
            id="declined"
            class="btn btn-warning"
            @click="handleModalButton(false)"
          >
            Abbrechen
          </button>
        </template>
      </MyModal>
    </template>
  </transition>
</template>

<script setup>
import { onMounted, ref, defineProps, watch } from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import PottingTable from "@/components/potting/PottingTable.vue";
import MyModal from "@/components/helpers/MyModal.vue";
import moment from "moment-timezone";
import { instance as axios } from "@/services/axios";

const props = defineProps({
  stationId: {
    type: Number,
    required: true,
  },
  tableUrl: {
    type: String,
    required: true,
  },
});

const componentKey = ref(0);
const productionDate = ref(new Date().toISOString());
const batchNumber = ref();
const modalMessage =
  "Das Produktionsdatum der Komponente ist älter als Acht Monate. Trotzdem speichern?";

let operator = ref();
let components = ref([
  { id: 1, text: "A-Komponente", value: "A" },
  { id: 2, text: "B-Komponente", value: "B" },
]);
let selectedComponent = ref("");

let datePickerLabel = ref("Produktionsdatum der Komponente: ");
let isGoodEntrance = ref();

const operators = ref([]);

// trigger values for alerts
let triggerSuccess = ref(false);
let triggerError = ref(false);
let triggerWarning = ref(false);
let triggerAbort = ref(false);
let triggerDateExpired = ref(false);

let showModal = ref(false);
let modalResolve;

const handleModal = () => {
  showModal.value = true;
  return new Promise((resolve) => {
    modalResolve = resolve;
  });
};

const handleModalButton = (value) => {
  showModal.value = false;
  modalResolve(value);
};

// watch function for scanner part
watch(batchNumber, (newValue) => {
  if (newValue && newValue.includes("$")) {
    batchNumber.value = newValue.slice(newValue.indexOf("$") + 1);
  }
});
document.addEventListener("keydown", (event) => {
  if (event.key === "$") {
    batchNumber.value = "";
  }
});
watch(
  productionDate,
  () => {
    const currentDate = new Date();
    const production = new Date(productionDate.value);

    // Stelle sicher, dass die Zeitkomponente ignoriert wird, indem du die Zeit auf Mitternacht setzt
    currentDate.setHours(0, 0, 0, 0);
    production.setHours(0, 0, 0, 0);

    // Berechne die Differenz in Jahren und Monaten
    let yearsDifference = currentDate.getFullYear() - production.getFullYear();
    let monthsDifference = currentDate.getMonth() - production.getMonth();

    let totalMonthsDifference = yearsDifference * 12 + monthsDifference;

    // Wenn der aktuelle Tag des Monats kleiner ist als der Produktions-Tag des Monats,
    // reduziere die Differenz um einen Monat
    if (currentDate.getDate() < production.getDate()) {
      totalMonthsDifference--;
    }

    triggerDateExpired.value = totalMonthsDifference >= 8;
  },
  { immediate: true }
);

onMounted(async () => {
  try {
    const { data } = await axios.get("/User/GetUsersByJobtype/3");
    operators.value = data
      .map(({ id, username }) => ({ id, username }))
      .sort((a, b) => a.username.localeCompare(b.username));
  } catch (error) {
    console.log(error.message);
  }
});

// reset the form after submit it
function resetForm() {
  operator.value = null;
  batchNumber.value = "";
  productionDate.value = null;
  selectedComponent.value = "";
  componentKey.value += 1;
  document.getElementById("goodEntrance").checked = false;
}

// submit the form for adding a new potting
async function submit() {
  const currentDate = moment().tz("Europe/Zurich").format();

  if (
    !document.getElementById("form").checkValidity() ||
    isGoodEntrance.value == null ||
    productionDate.value == null ||
    operator.value == null ||
    selectedComponent.value === "" ||
    batchNumber.value == null
  ) {
    //warning alert
    triggerWarning.value = true;
    setTimeout(() => (triggerWarning.value = false), 5000);
    return;
  }
  if (triggerDateExpired.value) {
    const modalResult = await handleModal();
    if (!modalResult) {
      // abort alert
      triggerAbort.value = true;
      setTimeout(() => (triggerAbort.value = false), 5000);
      return;
    }
  }
  //only tak Lot No. of batchNumber
  const batchNumberParsed = batchNumber.value.split(",")[2];

  // json object for the POST-Request
  let jsonObject = {
    operatorid: operator.value,
    stationid: props.stationId,
    dateInProduction: currentDate,
    pottingBatch: batchNumberParsed,
    manufacturingDate: productionDate.value,
    component: selectedComponent.value,
    isGoodEntrance: isGoodEntrance.value,
  };

  try {
    const response = await axios.post("/Material/AddPotting", jsonObject);
    console.log("Erfolgreiche Antwort:", response.data);
    resetForm();
  } catch (e) {
    console.log("Ein Fehler ist aufgetreten:", e.message);
    setTimeout(() => (triggerError.value = false), 5000);
    resetForm();
  }
  triggerSuccess.value = true;
  setTimeout(() => (triggerSuccess.value = false), 5000);
}
</script>

<style>
.alert-enter-active,
.alert-leave-active,
.modal-enter-active {
  transition: opacity 0.5s ease;
}
.alert-enter-from,
.alert-leave-to,
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

input[type="radio"] {
  transform: scale(1.3);
}
input[type="checkbox"] {
  transform: scale(1.2);
}
.radio-input {
  margin-right: 10px; /* Abstand zwischen dem Radio-Button und dem Label */
}
</style>
